import React, { useEffect } from 'react';
import { useStore, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { defer } from 'lodash';
import { getTranslation } from '../../providers/TranslationProvider';

import SearchFilter from './SearchFilter';
import { getPageContext } from '../../utils';
import TargetedDelivery from '../Experiments/TargetedDelivery';
import useExperiment from '../Experiments/useExperiment';
import { IS_JCE_BRIDGE_ENABLED, JCE_INTEGRATION_WINERY } from '../../constants/optimizelyExperiments';

import './SearchFilterContainer.scss';
import {
  CUISINE, LIST_ID, RESY_CREDIT_ELIGIBLE_ONLY, TABLE_AREA, VENUE_TYPE,
} from './searchFilterConstants';

const FILTER_TYPE = {
  cuisines: 'cuisines', lists: 'lists', resy_credit: 'resy_credit', seating: 'seating', venue_type: 'type',
};
const PARAM_CONSTANTS = ['seats', 'date', 'time'];

const SearchFilterContainer = ({
  $rootScope, Analytics, cuisines, isAuthenticated, isMobile, lists, seating, urlSlug,
}) => {
  const store = useStore();
  const [isJceBridgeEnabled] = useExperiment(IS_JCE_BRIDGE_ENABLED);
  const isWineRegion = useSelector(
    (state) => state.resyLocations?.bySlug?.[urlSlug]?.is_wine_region === true
  );
  const VENUE_TYPES = [
    { name: getTranslation({ translationKey: 'venue_type_search_pill_restaurants', fallback: 'Restaurants' }), value: 'restaurants' },
    { name: getTranslation({ translationKey: 'venue_type_search_pill_wineries', fallback: 'Wineries' }), value: 'wineries' },
  ];

  useEffect(() => {
    if (isMobile && $rootScope.hideSelectors) {
      $rootScope.setHideSelectors(false);
    } else if (!isMobile && !$rootScope.hideSelectors) {
      $rootScope.setHideSelectors(true);
    }
  }, [isMobile]);

  const updateFilter = (filterType, filterValue, analyticsFn) => {
    const url = new URL(window.location.href);
    url.pathname = `/cities/${urlSlug}/search`;
    const params = url.searchParams;

    // remove unnecessary query params
    params.forEach((_, name) => {
      if (!PARAM_CONSTANTS.includes(name)) params.delete(name);
    });

    if (filterType && filterValue) params.set(filterType, filterValue);
    const decodedHref = url.href.replaceAll('%3A', ':');
    window.history.pushState({}, null, decodedHref);
    defer(() => $rootScope.$apply());
    // Defer sending analytics until after the search has applied, to properly sync searchSessionId
    defer(analyticsFn);
  };

  const onCuisineClick = (item) => {
    updateFilter('facet', `${CUISINE}:${item.name}`, () => Analytics.event('complete search', {
      context: getPageContext(window.location.pathname),
      'filter type': 'search filter',
      value: 'cuisine',
      searchSessionId: store.getState().venueSearch.metadata?.searchSessionId,
    }));
  };

  const onListClick = (item) => {
    updateFilter('facet', `${LIST_ID}:${item.id}`);
  };

  const onSeatingClick = (item) => {
    updateFilter('facet', `${TABLE_AREA}:${item.value ?? item.name}`, () => Analytics.event('complete search', {
      context: getPageContext(window.location.pathname),
      'filter type': 'search filter',
      value: 'seating',
      searchSessionId: store.getState().venueSearch.metadata?.searchSessionId,
    }));
  };

  const onResyCreditToggle = () => {
    const url = new URL(window.location.href);
    const facet = url.searchParams.get('facet');
    const isActive = facet === `${RESY_CREDIT_ELIGIBLE_ONLY}:true`;
    if (isActive) {
      updateFilter();
    } else {
      updateFilter('facet', `${RESY_CREDIT_ELIGIBLE_ONLY}:true`, () => Analytics.event('complete search', {
        context: getPageContext(window.location.pathname),
        'filter type': 'search filter',
        value: 'resy_credit',
        searchSessionId: store.getState().venueSearch.metadata?.searchSessionId,
      }));
    }
  };
  const [isJceIntegrationWineryEnabled] = useExperiment(JCE_INTEGRATION_WINERY);
  const isVenueTypeEnabled = isJceBridgeEnabled && isJceIntegrationWineryEnabled && isWineRegion;
  const onVenueTypeClick = (item) => {
    const currentFacet = new URL(window.location.href).searchParams.get('facet');
    const selectedFacet = `${VENUE_TYPE}:${item.value}`;
    const isActive = currentFacet === selectedFacet;
    if (isActive) {
      updateFilter(undefined, undefined, () => Analytics.event('complete search', {
        context: getPageContext(window.location.pathname),
        'filter type': 'search filter',
        value: 'venue_type',
        searchSessionId: store.getState().venueSearch.metadata?.searchSessionId,
      }));
      return;
    }
    updateFilter('facet', `${VENUE_TYPE}:${item.value}`, () => Analytics.event('complete search', {
      context: getPageContext(window.location.pathname),
      'filter type': 'search filter',
      value: 'venue_type',
      searchSessionId: store.getState().venueSearch.metadata?.searchSessionId,
    }));
  };

  const getTitle = (type) => {
    let title = type.charAt(0).toUpperCase() + type.slice(1);
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const facet = params.get('facet');
    if (facet) {
      const facetKey = facet.split(':')[0];
      const facetValue = facet.split(':')[1];

      if (facetKey === LIST_ID && type === FILTER_TYPE.lists) {
        lists.map((currentList) => {
          if (currentList.id === facetValue) {
            title = currentList.name;
          }
          return title;
        });
      } else if ((facetKey === CUISINE && type === FILTER_TYPE.cuisines) || (facetKey === TABLE_AREA && type === FILTER_TYPE.seating)) {
        title = facetValue;
      } else if (facetKey === VENUE_TYPE && type === FILTER_TYPE.venue_type) {
        const selectedType = VENUE_TYPES.find((venueType) => venueType.value === facetValue);
        title = selectedType?.name || title;
      }
    }
    return title;
  };

  return (
    <div
      className={cx('SearchFilterContainer', { 'SearchFilterContainer--jce-bridge': isJceBridgeEnabled })}
      data-test-id="search_filter_container"
    >
      <TargetedDelivery flagKey={IS_JCE_BRIDGE_ENABLED}>
        <SearchFilter
          $rootScope={$rootScope}
          isMobile={isMobile}
          onFilterClick={onResyCreditToggle}
          title={getTranslation({
            translationKey: 'search_filter_resy_credit',
            fallback: 'Resy Credit',
          })}
          translationKey="search_filter_resy_credit"
          type={FILTER_TYPE.resy_credit}
        />
      </TargetedDelivery>
      {isVenueTypeEnabled && (
        <SearchFilter
          $rootScope={$rootScope}
          Analytics={Analytics}
          dropdownTitle="Venue Type"
          dropdownTranslationKey="search_filter_venue_type"
          isMobile={isMobile}
          onFilterClick={onVenueTypeClick}
          title={getTitle(FILTER_TYPE.venue_type)}
          translationKey="button_type"
          type={FILTER_TYPE.venue_type}
          values={VENUE_TYPES}
          updateFilter={updateFilter}
        />
      )}
      <SearchFilter
        $rootScope={$rootScope}
        Analytics={Analytics}
        isMobile={isMobile}
        onFilterClick={onCuisineClick}
        title={getTitle(FILTER_TYPE.cuisines)}
        translationKey={`button_${FILTER_TYPE.cuisines}`}
        type={FILTER_TYPE.cuisines}
        values={cuisines}
        updateFilter={updateFilter}
      />
      <SearchFilter
        $rootScope={$rootScope}
        Analytics={Analytics}
        isMobile={isMobile}
        onFilterClick={onSeatingClick}
        title={getTitle(FILTER_TYPE.seating)}
        translationKey={`button_${FILTER_TYPE.seating}`}
        type={FILTER_TYPE.seating}
        values={seating}
        updateFilter={updateFilter}
      />
      <SearchFilter
        $rootScope={$rootScope}
        Analytics={Analytics}
        isAuthenticated={isAuthenticated}
        isMobile={isMobile}
        onFilterClick={onListClick}
        title={getTitle(FILTER_TYPE.lists)}
        translationKey={`${FILTER_TYPE.lists}.title`}
        type={FILTER_TYPE.lists}
        values={lists}
        updateFilter={updateFilter}
      />
    </div>
  );
};

SearchFilterContainer.propTypes = {
  $rootScope: PropTypes.shape({
    $apply: PropTypes.func.isRequired,
    setHideSelectors: PropTypes.func.isRequired,
    hideSelectors: PropTypes.bool,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  cuisines: PropTypes.arrayOf(PropTypes.object),
  isAuthenticated: PropTypes.bool,
  isMobile: PropTypes.bool,
  lists: PropTypes.arrayOf(PropTypes.object),
  seating: PropTypes.arrayOf(PropTypes.object),
  urlSlug: PropTypes.string.isRequired,
};

SearchFilterContainer.defaultProps = {
  cuisines: undefined,
  isAuthenticated: false,
  isMobile: true,
  lists: undefined,
  seating: undefined,
};

SearchFilterContainer.displayName = 'SearchFilterContainer';

export default SearchFilterContainer;
